import type { ErrorComponentProps } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { CookieBanner } from "../components/CookieBanner";
import { InstallAppPrompt } from "../components/InstallAppPrompt";
import { PaymentTestModeBanner } from "../components/PaymentTestModeBanner";
import { useAuth } from "../hooks/use-auth";
import { useLang } from "../hooks/use-lang";
import { AUTH_T } from "../lib/auth-i18n";
import { User, LogOut, LogIn, Bookmark, Crown, Package, Instagram, Sparkles, Zap, Handshake } from "lucide-react";
import logo from "../assets/routify-logo-v2.png.asset.json";
import { Toaster } from "../components/ui/sonner";
import { supabase } from "../integrations/supabase/client";
import { safeLocalStorageGet } from "../lib/safe-browser";
import { requestPwaRecovery, setupPwaRecovery } from "../lib/pwa-recovery";
import { StaleBuildNotice } from "../components/StaleBuildNotice";
import { installClientErrorReporter } from "../lib/client-error-reporter";
import { getMyAffiliateBadge } from "../lib/affiliates.functions";
import { useServerFn } from "@tanstack/react-start";

const GA_MEASUREMENT_ID = "G-4M8E90N51W";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

function useGAPageTracker() {
  const router = useRouter();
  const lastPath = useRef<string>("");

  useEffect(() => {
    const handleLocationChange = () => {
      const currentPath = router.state.location.pathname;
      if (currentPath === lastPath.current) return;
      lastPath.current = currentPath;

      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "page_view", {
          page_path: currentPath,
          page_title: document.title,
        });
      }
    };

    handleLocationChange();
    return router.subscribe("onResolved", handleLocationChange);
  }, [router]);
}

function SiteHeader() {
  const { user, isAuthenticated, isLoading, signOut } = useAuth();
  const { lang } = useLang();
  const t = AUTH_T[lang];

  const loadBadge = useServerFn(getMyAffiliateBadge);
  const [isAffiliate, setIsAffiliate] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) { setIsAffiliate(false); return; }
    let alive = true;
    loadBadge().then((r) => { if (alive && r.isAffiliate) setIsAffiliate(true); }).catch(() => {});
    return () => { alive = false; };
  }, [isAuthenticated, loadBadge]);

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3.5">
        <Link to="/" className="flex items-center gap-2 group" aria-label="Routify — Início">
          <img src={logo.url} alt="Routify" className="h-9 w-auto transition-transform group-hover:scale-[1.03]" />
        </Link>

        {isLoading && !isAuthenticated ? (
          <div className="h-8 w-[88px] rounded-full bg-muted" aria-hidden="true" />
        ) : isAuthenticated ? (
          <div className="flex items-center gap-2">
            {isAffiliate && (
              <Link
                to="/affiliate"
                className="inline-flex items-center gap-1.5 rounded-full border border-blue-300 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-800 transition-colors hover:bg-blue-100"
                aria-label="Painel de Afiliado"
                title="Painel de Afiliado"
              >
                <Handshake className="h-4 w-4" />
                <span className="hidden sm:inline">Afiliado</span>
              </Link>
            )}
            <Link
              to="/my-routes"
              className="inline-flex items-center gap-1.5 rounded-full border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              <Bookmark className="h-4 w-4" />
              <span className="hidden sm:inline">As minhas rotas</span>
            </Link>
            <a
              href="/credits"
              className="inline-flex items-center gap-1.5 rounded-full border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              aria-label="Packs de rotas"
              title="Packs de rotas"
            >
              <Package className="h-4 w-4" />
              <span className="hidden md:inline">Packs</span>
            </a>
            <Link
              to="/account"
              className="inline-flex items-center gap-1.5 rounded-full border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              aria-label="A minha conta"
              title="A minha conta"
            >
              <Crown className="h-4 w-4" />
              <span className="hidden sm:inline">Conta</span>
            </Link>
            <span className="hidden sm:inline-flex items-center gap-1 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              {user?.email?.split("@")[0]}
            </span>
            <button
              onClick={signOut}
              type="button"
              className="inline-flex items-center gap-1.5 rounded-full border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t.signOut}</span>
            </button>
          </div>
        ) : (
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            aria-busy={isLoading ? "true" : undefined}
          >
            <LogIn className="h-4 w-4" />
            {t.signIn}
          </Link>
        )}
      </div>
    </header>
  );
}

const NAV_T: Record<string, { custom: string; pro: string }> = {
  en: { custom: "Custom planning", pro: "Routify Pro" },
  fr: { custom: "Planification personnalisée", pro: "Routify Pro" },
  es: { custom: "Planificación personalizada", pro: "Routify Pro" },
  pt: { custom: "Planeamento personalizado", pro: "Routify Pro" },
  it: { custom: "Pianificazione personalizzata", pro: "Routify Pro" },
  de: { custom: "Individuelle Reiseplanung", pro: "Routify Pro" },
  ja: { custom: "Custom planning", pro: "Routify Pro" },
};

function ToolsNav() {
  const { lang } = useLang();
  const t = NAV_T[lang] ?? NAV_T.en;


  return (
    <nav className="border-b border-border/60 bg-background px-4 py-2">
      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-2">
        <Link
          to="/concierge"
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-input bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          aria-label={t.custom}
          title={t.custom}
        >
          <Sparkles className="h-4 w-4 text-brand shrink-0" />
          <span className="truncate">{t.custom}</span>
        </Link>
        <Link
          to="/pro"
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-brand/40 bg-brand/5 px-3 py-2 text-sm font-medium text-brand transition-colors hover:bg-brand/10"
          aria-label={t.pro}
          title={t.pro}
        >
          <Zap className="h-4 w-4 shrink-0" />
          <span className="truncate">{t.pro}</span>
        </Link>
      </div>
    </nav>

  );
}

const FOOTER_T: Record<string, { rights: string; contacts: string; social: string; legal: string; terms: string; privacy: string; cookies: string }> = {
  en: { rights: "All rights reserved.", contacts: "Contact", social: "Social", legal: "Legal", terms: "Terms", privacy: "Privacy", cookies: "Cookies" },
  fr: { rights: "Tous droits réservés.", contacts: "Contact", social: "Réseaux sociaux", legal: "Mentions légales", terms: "Conditions", privacy: "Confidentialité", cookies: "Cookies" },
  es: { rights: "Todos los derechos reservados.", contacts: "Contacto", social: "Redes sociales", legal: "Legal", terms: "Términos", privacy: "Privacidad", cookies: "Cookies" },
  pt: { rights: "Todos os direitos reservados.", contacts: "Contactos", social: "Redes sociais", legal: "Legal", terms: "Termos", privacy: "Privacidade", cookies: "Cookies" },
  de: { rights: "Alle Rechte vorbehalten.", contacts: "Kontakt", social: "Soziale Netzwerke", legal: "Impressum", terms: "AGB", privacy: "Datenschutz", cookies: "Cookies" },
  ja: { rights: "無断複写・転載を禁じます。", contacts: "お問い合わせ", social: "ソーシャル", legal: "法的情報", terms: "利用規約", privacy: "プライバシー", cookies: "Cookie" },
};

function SiteFooter() {
  const { lang } = useLang();
  const t = FOOTER_T[lang] ?? FOOTER_T.en;
  const router = useRouter();
  const pathname = router.state.location.pathname;
  const showPartnerCta = pathname === "/";
  return (
    <>
      {showPartnerCta ? (
        <section className="border-t border-border bg-gradient-to-br from-brand/10 via-background to-background px-6 py-10">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand">
              <Handshake className="h-3.5 w-3.5" /> Programa de parceiros
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Tens uma comunidade ligada a viagens?
            </h2>
            <p className="max-w-2xl text-sm text-muted-foreground sm:text-base">
              Junta-te à Routify. Recebe um código promocional exclusivo com desconto para os teus seguidores e comissão
              sobre cada venda — com dashboard própria para acompanhares tudo em tempo real.
            </p>
            <Link
              to="/partner"
              className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-brand-foreground shadow transition hover:opacity-90"
            >
              <Handshake className="h-4 w-4" /> Candidatar-me agora
            </Link>
          </div>
        </section>
      ) : null}
    <footer className="border-t border-border bg-background/60 px-6 py-8 text-xs text-muted-foreground">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          <p className="text-sm font-semibold text-foreground">Routify</p>
          <p>© {new Date().getFullYear()} Routify. {t.rights}</p>
        </div>

        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground">{t.contacts}</p>
          <div className="flex flex-col gap-1">
            <a href="mailto:suporte@routify-tour.com" className="hover:text-foreground">
              suporte@routify-tour.com
            </a>
            <a href="mailto:parcerias@routify-tour.com" className="hover:text-foreground">
              parcerias@routify-tour.com
            </a>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground">{t.social}</p>
          <div className="flex items-center gap-3">
            <a
              href="https://www.instagram.com/routify.travel?igsh=MW01c2l5cm1zcnMwMw=="
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border transition hover:bg-muted hover:text-foreground"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <a
              href="https://www.tiktok.com/@routify.travel?_r=1&_t=ZN-97gBPLWlylS"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border transition hover:bg-muted hover:text-foreground"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                <path d="M16.5 3a5.5 5.5 0 0 0 4.5 4.5v3a8.4 8.4 0 0 1-4.5-1.4v6.4a6 6 0 1 1-6-6c.3 0 .7 0 1 .1v3.1a3 3 0 1 0 2 2.8V3z"/>
              </svg>
            </a>
          </div>
        </div>

        <nav className="flex flex-wrap items-start gap-4">
          <Link to="/partner" className="hover:text-foreground">Tornar-me parceiro</Link>
          <Link to="/legal" className="hover:text-foreground">{t.legal}</Link>
          <Link to="/terms" className="hover:text-foreground">{t.terms}</Link>
          <Link to="/privacy" className="hover:text-foreground">{t.privacy}</Link>
          <Link to="/cookies" className="hover:text-foreground">{t.cookies}</Link>
        </nav>
      </div>
    </footer>
    </>
  );
}


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();

  useEffect(() => {
    requestPwaRecovery(error, "route-error-boundary");
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          A página não carregou
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Algo correu mal ao abrir esta vista. Tenta novamente ou volta ao início.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Tentar novamente
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Ir para o início
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "theme-color", content: "#ffffff" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "Routify" },
      { name: "mobile-web-app-capable", content: "yes" },
      { title: "Routify — Smart Multimodal Tourist Route Planner" },
      { name: "description", content: "Plan walking, e-scooter, and bicycle routes anywhere in the world. Multilingual, GPS-aware, ready for live Google Maps and GBFS data." },
      { name: "author", content: "Routify" },
      { property: "og:title", content: "Routify — Smart Multimodal Tourist Route Planner" },
      { property: "og:description", content: "Plan walking, e-scooter, and bicycle routes anywhere in the world. Multilingual, GPS-aware, ready for live Google Maps and GBFS data." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Routify" },
      { property: "og:url", content: "https://www.routify-tour.com/" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Routify — Smart Multimodal Tourist Route Planner" },
      { name: "twitter:description", content: "Plan walking, e-scooter, and bicycle routes anywhere in the world. Multilingual, GPS-aware, ready for live Google Maps and GBFS data." },
      { name: "robots", content: "index, follow" },
      { name: "googlebot", content: "index, follow" },
      { name: "google-site-verification", content: "-tZXgBZ_pauh-eEZGzQ0aBNi2ndjJ22gIMe0AQ1Jyts" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap",
      },
      {
        rel: "icon",
        type: "image/x-icon",
        href: "/favicon.ico?v=20260719",
        sizes: "any",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "96x96",
        href: "/favicon-96.png?v=20260719",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "192x192",
        href: "/favicon-192-solid.png?v=20260719",
      },
      {
        rel: "shortcut icon",
        href: "/favicon.ico?v=20260719",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png?v=20260709-transparent",
      },
      {
        rel: "manifest",
        href: "/manifest.webmanifest?v=20260709-transparent",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Routify",
          url: "https://www.routify-tour.com",
          logo: "https://www.routify-tour.com/apple-touch-icon.png",
          sameAs: [
            "https://www.instagram.com/routify.travel",
            "https://www.tiktok.com/@routify.travel",
          ],
          contactPoint: [{
            "@type": "ContactPoint",
            email: "suporte@routify-tour.com",
            contactType: "customer support",
            availableLanguage: ["Portuguese", "English", "Spanish", "French", "German", "Japanese"],
          }],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Routify",
          url: "https://www.routify-tour.com",
          inLanguage: ["pt-PT","en","es","fr","de","ja"],
          potentialAction: {
            "@type": "SearchAction",
            target: "https://www.routify-tour.com/?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Routify",
          applicationCategory: "TravelApplication",
          operatingSystem: "Web, iOS, Android",
          url: "https://www.routify-tour.com",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "EUR",
          },
          description: "Smart multimodal tourist route planner with GPS, Google Maps and AI-powered itineraries.",
        }),
      },
    ],

    // Google Analytics is NOT loaded here — it is loaded only after the
    // user accepts non-essential cookies in the CookieBanner. See GAConsentLoader.
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function AuthSync() {
  const router = useRouter();
  const queryClient = useQueryClient();
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    });
    return () => subscription.unsubscribe();
  }, [router, queryClient]);
  return null;
}

function GAConsentLoader() {
  useEffect(() => {
    let loaded = false;

    function loadGA() {
      if (loaded) return;
      if (typeof window === "undefined") return;
      try {
        if (safeLocalStorageGet("routify_cookie_consent") !== "accepted") return;
      } catch { return; }
      loaded = true;

      window.dataLayer = window.dataLayer || [];
      const gtag = (...args: unknown[]) => { window.dataLayer!.push(args); };
      window.gtag = gtag as typeof window.gtag;
      gtag("js", new Date());
      gtag("config", GA_MEASUREMENT_ID, { send_page_view: false, anonymize_ip: true });

      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(s);
    }

    loadGA();
    const handler = () => loadGA();
    window.addEventListener("routify-consent-change", handler);
    return () => window.removeEventListener("routify-consent-change", handler);
  }, []);
  return null;
}

function PwaRecoveryBootstrap() {
  useEffect(() => {
    setupPwaRecovery();
  }, []);

  return null;
}

function ErrorReporterBootstrap() {
  useEffect(() => {
    installClientErrorReporter();
    import("../lib/sentry.browser").then(({ initSentryClient, setSentryUser }) => {
      initSentryClient();
      supabase.auth.getSession().then(({ data }) => {
        const u = data.session?.user;
        setSentryUser(u ? { id: u.id, email: u.email } : null);
      }).catch(() => {});
      supabase.auth.onAuthStateChange((_e, s) => {
        const u = s?.user;
        setSentryUser(u ? { id: u.id, email: u.email } : null);
      });
    }).catch(() => {});
  }, []);
  return null;
}

function AffiliateRefBootstrap() {
  useEffect(() => {
    import("../lib/affiliate-ref").then(({ captureAffiliateRefFromUrl }) => {
      captureAffiliateRefFromUrl().catch(() => {});
    }).catch(() => {});
  }, []);
  return null;
}

function NativeDeepLinkBootstrap() {
  const router = useRouter();
  useEffect(() => {
    import("../lib/native-deep-links").then(({ registerNativeDeepLinkHandler }) => {
      return registerNativeDeepLinkHandler((url) => {
        try {
          const parsed = new URL(url);
          if (parsed.protocol !== "routify:") return;
          const path = parsed.pathname || "/";
          const searchParams = new URLSearchParams(parsed.search);
          const search: Record<string, string> = {};
          searchParams.forEach((value, key) => {
            search[key] = value;
          });
          void router.navigate({ to: path, search });
        } catch {
          // Ignore malformed deep links.
        }
      });
    }).catch(() => {});
  }, [router]);
  return null;
}


function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useGAPageTracker();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthSync />
      <PwaRecoveryBootstrap />
      <ErrorReporterBootstrap />
      <AffiliateRefBootstrap />
      <NativeDeepLinkBootstrap />
      <GAConsentLoader />
      <PaymentTestModeBanner />
      <SiteHeader />
      <ToolsNav />
      <Outlet />
      <SiteFooter />
      <InstallAppPrompt />
      <CookieBanner />
      <StaleBuildNotice />
      <Toaster />
    </QueryClientProvider>
  );
}
